import React, { useState } from 'react';
import { Box } from '@mui/material';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';

import { Tooltip } from './components';

export function Day(props: PickersDayProps<Dayjs> & {
  workTimePeriod?: any,
  holidayPeriod?: any,
  dayOffPeriod?: any,
}) {
  const {
    workTimePeriod = {},
    holidayPeriod = {},
    dayOffPeriod = {},
    outsideCurrentMonth,
    day,
    ...rest
  } = props;

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const handleTooltipOpen = () => {
    setIsTooltipOpen(true);
  };
  const handleTooltipClose = () => {
    setIsTooltipOpen(false);
  };

  const dayFormatted = day.format('YYYY-MM-DD');
  const highlightedWeekDays = workTimePeriod.weekDays;
  const highlightedTimePeriod = workTimePeriod.timePeriod;
  const weekDay = day.day() || 7;

  const isStartDate = !outsideCurrentMonth && day.diff(dayjs(workTimePeriod?.startDate).format('YYYY-MM-DD'), 'day') === 0;
  const isBetweenDate = day.diff(dayjs(workTimePeriod?.startDate).format('YYYY-MM-DD'), 'day') >= 0
    && day.diff(dayjs(workTimePeriod?.endDate).format('YYYY-MM-DD'), 'day') <= 0;
  const isEndDate = !outsideCurrentMonth && day.diff(dayjs(workTimePeriod?.endDate).format('YYYY-MM-DD'), 'day') === 0;

  const highlightedHoliday = holidayPeriod?.[dayFormatted];
  const highlightedDayOff = dayOffPeriod?.[dayFormatted];

  let isShouldShowBackground = false;
  let isHoliday = false;
  let isDayOff = false;
  if (isBetweenDate && highlightedWeekDays.includes(weekDay)) {
    isShouldShowBackground = true;
    isHoliday = !!highlightedHoliday;
    isDayOff = !!highlightedDayOff;
  }

  const getBorderRadiusStyle = () => {
    if (isStartDate) {
      return {
        borderTopLeftRadius: '50%',
        borderBottomLeftRadius: '50%',
        backgroundColor: 'transparent',
        background: 'linear-gradient(to left, rgba(191, 41, 92, 0.15), rgba(191, 41, 92, 0.15)) no-repeat 18px 0px;'
      };
    } else if (isEndDate) {
      return {
        borderTopRightRadius: '50%',
        borderBottomRightRadius: '50%',
        backgroundColor: 'transparent',
        background: 'linear-gradient(to right, rgba(191, 41, 92, 0.15), rgba(191, 41, 92, 0.15)) no-repeat -18px 0px;'
      };
    }

    let styleObj = {};

    if (highlightedWeekDays.includes(weekDay)) {
      const indexWeek = highlightedWeekDays.indexOf(weekDay);

      if (indexWeek === 0) {
        styleObj = {
          ...styleObj,
          borderTopLeftRadius: '4px',
          borderBottomLeftRadius: '4px',
        };
      }
      if (indexWeek === highlightedWeekDays.length - 1) {
        styleObj = {
          ...styleObj,
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px',
        };
      }
      if (weekDay + 1 !== highlightedWeekDays[indexWeek + 1]) {
        styleObj = {
          ...styleObj,
          borderTopRightRadius: '4px',
          borderBottomRightRadius: '4px',
        };
      }
      if (weekDay - 1 !== highlightedWeekDays[indexWeek - 1]) {
        styleObj = {
          ...styleObj,
          borderTopLeftRadius: '4px',
          borderBottomLeftRadius: '4px',
        };
      }
    }

    return styleObj;
  };

  return (
    <Box sx={{
      lineHeight: 1,
      backgroundColor: isShouldShowBackground ? 'rgba(191, 41, 92, 0.15)' : undefined,
      ...getBorderRadiusStyle(),
    }}>
      {
        isShouldShowBackground ? (
          <Tooltip
            isOpen={isTooltipOpen}
            onClose={handleTooltipClose}
            day={day}
            workTimePeriod={highlightedTimePeriod[weekDay]}
            dayOffPeriod={highlightedDayOff}
            holidayPeriod={highlightedHoliday}
          >
            <PickersDay
              {...rest}
              day={day}
              onFocus={(event) => {
                event.stopPropagation();
              }}
              onKeyDown={(event) => {
                event.stopPropagation();
              }}
              outsideCurrentMonth={outsideCurrentMonth}
              onClick={handleTooltipOpen}
              disableRipple
              selected={isStartDate || isEndDate}
              className={isHoliday ? 'holiday' : isDayOff ? 'dayOff' : undefined}
            />
          </Tooltip>
        ) : (
          <PickersDay
            {...rest}
            day={day}
            onFocus={(event) => {
              event.stopPropagation();
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
            outsideCurrentMonth={outsideCurrentMonth}
            disableRipple
            selected={isStartDate || isEndDate}
          />
        )
      }
    </Box>
  );
}
